import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Container,
  Row,
  Col,
  Form,
  Button,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
  Modal,
  Offcanvas,
  Accordion,
  Badge,
  Spinner,
  Dropdown,
} from "react-bootstrap";
import { getUserPracticeById } from "../../api";
import { getAgeAnalysis, getAgeAnalysisExport } from "../../afinanceapi";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import "./CreateInvoice.css"; // Assuming you have some styles in this file
import "./acc-list.css";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function AgedInvoicesList() {
  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totals, setTotals] = useState({
    sumPending: 0,
    sum30: 0,
    sum60: 0,
    sum90: 0,
    sum120: 0,
    sumAll: 0,
  });
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Data for dropdowns
  const [practiceList, setPracticeList] = useState([]);

  // Committed filters (drive the table)
  const [selectedPractices, setSelectedPractices] = useState([]); // [] = All
  const [selectedStatuses, setSelectedStatuses] = useState([]); // [] = All
  const [selectedAging, setSelectedAging] = useState([]); // [], e.g. ["30","60"]
  // Optional date range (empty = show all aged invoices, as before). The range
  // filters EITHER the invoice date OR the createdAt — mutually exclusive.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateField, setDateField] = useState("invoice"); // 'invoice' | 'created'

  // Temporary filters while panel is open
  const [tmpPractices, setTmpPractices] = useState([]);
  const [tmpStatuses, setTmpStatuses] = useState([]);
  const [tmpAging, setTmpAging] = useState([]);
  const [tmpStartDate, setTmpStartDate] = useState("");
  const [tmpEndDate, setTmpEndDate] = useState("");
  const [tmpDateField, setTmpDateField] = useState("invoice");
  const [userRole, setUserRole] = useState(false); // True if user has allowed role

  // Grouping
  const [groupedView, setGroupedView] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(new Set()); // Set of accountName strings

  // Row accordion expansion
  const [expandedRows, setExpandedRows] = useState(new Set()); // Set of invoice _id strings

  // Export loading
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 20;

  // UI
  const [showInfo, setShowInfo] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const query = useQuery();
  const agingParam = query.get("aging");

  const currency = new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
  });

  // Status options shown in the filter panel come from the backend — the
  // distinct statuses present among outstanding invoices for the current
  // practice + date scope (see /invoices/ageAnalysis availableStatuses). This
  // replaces a hardcoded list that silently omitted compound statuses like
  // "Finalised - Not Paid" / "Processed - Partially Paid".
  // (Paid, Cancelled and Written Off are excluded at the backend for age
  //  analysis, so they never appear here.)
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const STATUS_FALLBACK = [
    "Pending",
    "Posted",
    "Posted - Claim in Progress",
    "Partially Paid",
    "Paid - Not Linked",
  ];
  const statusOptions = useMemo(() => {
    const base = availableStatuses.length ? availableStatuses : STATUS_FALLBACK;
    // Keep any currently-committed status visible even if a tighter date range
    // means it's momentarily absent from the scope, so it stays uncheckable-able.
    return Array.from(new Set([...base, ...selectedStatuses])).sort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableStatuses, selectedStatuses]);

  // Load practice list and user role once
  useEffect(() => {
    async function loadPractices() {
      try {
        const token = sessionStorage.getItem("user");
        if (!token) return;
        const user = jwtDecode(token);
        const rolesFromRole = Array.isArray(user?.role)
          ? user.role
          : user?.role
            ? [user.role]
            : [];
        const rolesFromPermissions = Array.isArray(user?.permissions)
          ? user.permissions
          : [];
        const roles = [...new Set([...rolesFromRole, ...rolesFromPermissions])];
        const allowedRoles = ["Practitioner", "Finance", "SuperAdmin"];
        setUserRole(roles.some((r) => allowedRoles.includes(r)));
        const practices = await getUserPracticeById(user._id);
        setPracticeList(practices || []);
      } catch (e) {
        console.error("Error loading practices:", e);
      }
    }
    loadPractices();
  }, []);

  // Backend-driven data fetch — fires on every filter/sort/page change
  useEffect(() => {
    if (practiceList.length === 0) return;

    async function fetchData() {
      try {
        setLoading(true);
        const practiceNumbers = practiceList.map((p) => p.practiceNumber);
        const result = await getAgeAnalysis({
          practiceNumbers,
          statuses: selectedStatuses,
          aging: selectedAging,
          practices: selectedPractices,
          startDate,
          endDate,
          dateField,
          sortKey: sortConfig.key,
          sortDir: sortConfig.direction,
          page: groupedView ? 1 : page,
          limit: groupedView ? 10000 : limit,
        });
        setInvoices(result.data || []);
        setTotalCount(result.total || 0);
        setTotals(
          result.totals || {
            sumPending: 0,
            sum30: 0,
            sum60: 0,
            sum90: 0,
            sum120: 0,
            sumAll: 0,
          },
        );
        if (Array.isArray(result.availableStatuses)) {
          setAvailableStatuses(result.availableStatuses);
        }
        setExpandedGroups(new Set());
        setExpandedRows(new Set());
      } catch (e) {
        console.error("Error fetching age analysis:", e);
        setInvoices([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [
    practiceList,
    selectedStatuses,
    selectedAging,
    selectedPractices,
    startDate,
    endDate,
    dateField,
    sortConfig,
    page,
    groupedView,
  ]);

  // Read URL params on mount to initialise committed filter state (per D-04)
  // Handles: practices, statuses, aging (unified — replaces aging-only useEffect)
  useEffect(() => {
    const p = query.get('practices');
    const s = query.get('statuses');
    const a = query.get('aging');
    const st = query.get('start');
    const en = query.get('end');
    const df = query.get('df');
    if (p) { const arr = p.split(','); setSelectedPractices(arr); setTmpPractices(arr); }
    if (s) { const arr = s.split(','); setSelectedStatuses(arr); setTmpStatuses(arr); }
    if (a) {
      const arr = a.split(',').filter(v => ['30','60','90','120'].includes(v));
      if (arr.length) { setSelectedAging(arr); setTmpAging(arr); }
    }
    if (st) { setStartDate(st); setTmpStartDate(st); }
    if (en) { setEndDate(en); setTmpEndDate(en); }
    if (df === 'created' || df === 'invoice') { setDateField(df); setTmpDateField(df); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helpers for multi-select toggles
  const toggleIn = (arr, value) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const allPractices = useMemo(
    () => (practiceList || []).map((p) => p.practiceName).filter(Boolean),
    [practiceList],
  );

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  // Reset page on filter change
  useEffect(() => {
    setPage(1);
  }, [selectedPractices, selectedStatuses, selectedAging, startDate, endDate, dateField]);

  // Grouping computation
  const groupedInvoices = useMemo(() => {
    if (!groupedView) return [];
    const map = new Map();
    for (const inv of invoices) {
      const key = inv.accountName || "(No Account)";
      if (!map.has(key)) {
        map.set(key, {
          accountName: key,
          accountId: inv.accountId,
          accountNumber: inv.accountNumber || "",
          invoices: [],
          sum30: 0,
          sum60: 0,
          sum90: 0,
          sum120: 0,
          sumPending: 0,
        });
      }
      const g = map.get(key);
      g.invoices.push(inv);
      g.sum30 += inv.days30 || 0;
      g.sum60 += inv.days60 || 0;
      g.sum90 += inv.days90 || 0;
      g.sum120 += inv.days120plus || 0;
      g.sumPending += inv.pending || 0;
    }
    return Array.from(map.values());
  }, [invoices, groupedView]);

  const toggleGroup = (accountName) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(accountName) ? next.delete(accountName) : next.add(accountName);
      return next;
    });
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <i className="fa-solid fa-arrow-up text-primary ms-2" />
    ) : (
      <i className="fa-solid fa-arrow-down text-primary ms-2" />
    );
  };

  // exportType: "full" (default — invoice headers + lines + receipts + credits + write-offs),
  // "summary" (invoice header rows only), or "lines" (line rows only).
  const handleExportExcel = async (exportType = "full") => {
    setShowExportModal(false);
    try {
      setExportLoading(true);
      const practiceNumbers = practiceList.map((p) => p.practiceNumber);
      const allInvoices = await getAgeAnalysisExport({
        practiceNumbers,
        statuses: selectedStatuses,
        aging: selectedAging,
        practices: selectedPractices,
        startDate,
        endDate,
        dateField,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
      });

      // Layout: one Invoice header row (with aged buckets + outstanding) followed by
      // Line / Receipt / Credit / Write-Off detail rows. Aged-bucket columns appear
      // only on the Invoice header rows so summing them in Excel reconciles to the
      // on-screen totals exactly.
      const exportData = allInvoices.reduce((acc, inv) => {
        const invDate = inv.invoiceDate ? new Date(inv.invoiceDate).toISOString().slice(0, 10) : "";
        const invCreated = inv.createdAt ? new Date(inv.createdAt).toISOString().slice(0, 10) : "";
        const practiceName = inv.practice?.practiceName || "";
        const lines = Array.isArray(inv.lines) ? inv.lines : [];
        const receipts = Array.isArray(inv.receipts) ? inv.receipts : [];
        const credits = receipts.filter((r) => r.creditNoteNumber);
        const writeOffs = receipts.filter((r) => r.writeOffNumber);
        const payments = receipts.filter(
          (r) => !r.creditNoteNumber && !r.writeOffNumber && Number(r.receiptAmount || 0) !== 0
        );

        // VAT is 15% in SA and only applies when the practice has a VAT number;
        // mirrors the backend totals calc (invoiceRoutes.js: hasVat check).
        const hasVat = !!inv.practice?.vatNumber;
        const vatRate = hasVat ? 15 : 0;
        const splitVat = (incl) => {
          const i = Number(incl) || 0;
          if (!hasVat) return { excl: i, vat: 0 };
          const excl = i / 1.15;
          return { excl, vat: i - excl };
        };

        // 1. Invoice header row — aggregated, carries the aged buckets
        const invSplit = splitVat(Number(inv.totalAmount) || 0);
        acc.push({
          RowType: "Invoice",
          Date: invDate,
          Created: invCreated,
          DateOfService: "",
          InvoiceNumber: inv.invoiceNumber || "",
          AccountName: inv.accountName || "",
          Status: inv.status || "",
          Reference: "",
          Description: "Invoice summary",
          Quantity: "",
          Rate: "",
          ServiceProvider: "",
          PracticeName: practiceName,
          Outstanding: Number(inv.totalAmount) || 0,
          Pending: inv.pending || 0,
          Days30: inv.days30 || 0,
          Days60: inv.days60 || 0,
          Days90: inv.days90 || 0,
          Days120plus: inv.days120plus || 0,
          ExclVat: invSplit.excl,
          VatAmount: invSplit.vat,
          VatRate: vatRate,
        });

        // 2. Line rows
        lines.forEach((line) => {
          const sp = line.serviceProvider
            ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
            : inv.practitioner?.firstName
              ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
              : "";
          // Date of service: same precedence as CreateInvoice's getLineServiceDate —
          // custom date > appointment-derived date > legacy > invoice date.
          const serviceDate = String(
            line.appointmentDateCustom ||
              line.appointmentDateSet ||
              line.appointmentDate ||
              inv.invoiceDate ||
              ""
          ).slice(0, 10);
          // line.rate is the line total (incl VAT if the practice is VAT-registered)
          // — see CreateInvoice totalAmount calc (sum of line.rate, no qty multiplier).
          const lineSplit = splitVat(Number(line.rate) || 0);
          acc.push({
            RowType: "Line",
            Date: invDate,
            DateOfService: serviceDate,
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: line.tariffCode || line.nappiCode || "",
            Description: line.description || "",
            Quantity: Number(line.quantity ?? 1),
            Rate: Number(line.rate || 0),
            ServiceProvider: sp,
            PracticeName: practiceName,
            Outstanding: "",
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
            ExclVat: lineSplit.excl,
            VatAmount: lineSplit.vat,
            VatRate: vatRate,
          });
        });

        // 3. Payment receipts (regular payments — previously missing from the export)
        payments.forEach((r) => {
          const net = (Number(r.receiptAmount) || 0) - (Number(r.refundAmount) || 0);
          const methodSuffix = r.receiptMethod ? ` — ${r.receiptMethod}` : "";
          const refundSuffix = r.refundAmount ? ` (refund R${Number(r.refundAmount).toFixed(2)} applied)` : "";
          const paySplit = splitVat(-net);
          acc.push({
            RowType: "Receipt",
            Date: r.receiptDate ? new Date(r.receiptDate).toISOString().slice(0, 10) : invDate,
            DateOfService: "",
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: r.receiptNumber || "",
            Description: `Payment${methodSuffix}${refundSuffix}`,
            Quantity: "",
            Rate: -net,
            ServiceProvider: "",
            PracticeName: practiceName,
            Outstanding: "",
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
            ExclVat: paySplit.excl,
            VatAmount: paySplit.vat,
            VatRate: vatRate,
          });
        });

        // 4. Credit notes — prefer stored creditNoteExclAmount/VATAmount when
        // present (issued by Credit Note flow); else fall back to splitting
        // creditNoteAmount at the VAT rate.
        credits.forEach((r) => {
          const cnIncl = -(Number(r.creditNoteAmount) || 0);
          let cnExcl;
          let cnVat;
          if (
            hasVat &&
            r.creditNoteExclAmount != null &&
            r.creditNoteVATAmount != null
          ) {
            cnExcl = -(Number(r.creditNoteExclAmount) || 0);
            cnVat = -(Number(r.creditNoteVATAmount) || 0);
          } else {
            const s = splitVat(cnIncl);
            cnExcl = s.excl;
            cnVat = s.vat;
          }
          acc.push({
            RowType: "Credit Note",
            Date: r.creditNoteDate ? new Date(r.creditNoteDate).toISOString().slice(0, 10) : invDate,
            DateOfService: "",
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: r.creditNoteNumber || "",
            Description: "Credit Note",
            Quantity: "",
            Rate: cnIncl,
            ServiceProvider: "",
            PracticeName: practiceName,
            Outstanding: "",
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
            ExclVat: cnExcl,
            VatAmount: cnVat,
            VatRate: vatRate,
          });
        });

        // 5. Write-offs
        writeOffs.forEach((r) => {
          const woIncl = -(Number(r.writeOffAmount) || 0);
          const woSplit = splitVat(woIncl);
          acc.push({
            RowType: "Write-Off",
            Date: r.writeOffDate ? new Date(r.writeOffDate).toISOString().slice(0, 10) : invDate,
            DateOfService: "",
            InvoiceNumber: inv.invoiceNumber || "",
            AccountName: inv.accountName || "",
            Status: "",
            Reference: r.writeOffNumber || "",
            Description: "Write-Off",
            Quantity: "",
            Rate: woIncl,
            ServiceProvider: "",
            PracticeName: practiceName,
            Outstanding: "",
            Pending: "",
            Days30: "",
            Days60: "",
            Days90: "",
            Days120plus: "",
            ExclVat: woSplit.excl,
            VatAmount: woSplit.vat,
            VatRate: vatRate,
          });
        });

        return acc;
      }, []);

      let rows = exportData;
      let sheetName = "Aged Invoices";
      let fileName = "aged-invoices.xlsx";
      if (exportType === "summary") {
        rows = exportData.filter((r) => r.RowType === "Invoice");
        sheetName = "Aged Invoices Summary";
        fileName = "aged-invoices-summary.xlsx";
      } else if (exportType === "lines") {
        rows = exportData.filter((r) => r.RowType === "Line");
        sheetName = "Invoice Lines";
        fileName = "invoice-lines.xlsx";
      }

      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(sheetName);

      const moneyFmt = '#,##0.00;[Red]-#,##0.00;""';

      ws.columns = [
        { header: "Row Type",         key: "RowType",         width: 12 },
        { header: "Date",             key: "Date",            width: 12 },
        { header: "Created",          key: "Created",         width: 12 },
        { header: "Date of Service",  key: "DateOfService",   width: 14 },
        { header: "Invoice #",        key: "InvoiceNumber",   width: 14 },
        { header: "Account Name",     key: "AccountName",     width: 30 },
        { header: "Status",           key: "Status",          width: 12 },
        { header: "Reference",        key: "Reference",       width: 14 },
        { header: "Description",      key: "Description",     width: 42 },
        { header: "Qty",              key: "Quantity",        width: 7  },
        { header: "Rate",             key: "Rate",            width: 13, style: { numFmt: moneyFmt } },
        { header: "Excl VAT",         key: "ExclVat",         width: 13, style: { numFmt: moneyFmt } },
        { header: "VAT",              key: "VatAmount",       width: 13, style: { numFmt: moneyFmt } },
        { header: "VAT %",            key: "VatRate",         width: 7  },
        { header: "Service Provider", key: "ServiceProvider", width: 22 },
        { header: "Practice Name",    key: "PracticeName",    width: 26 },
        { header: "Outstanding",      key: "Outstanding",     width: 13, style: { numFmt: moneyFmt } },
        { header: "Pending",          key: "Pending",         width: 12, style: { numFmt: moneyFmt } },
        { header: "30 Days",          key: "Days30",          width: 11, style: { numFmt: moneyFmt } },
        { header: "60 Days",          key: "Days60",          width: 11, style: { numFmt: moneyFmt } },
        { header: "90 Days",          key: "Days90",          width: 11, style: { numFmt: moneyFmt } },
        { header: "120+ Days",        key: "Days120plus",     width: 12, style: { numFmt: moneyFmt } },
      ];

      ws.addRows(rows);

      ws.getRow(1).font = { bold: true };
      ws.views = [{ state: "frozen", ySplit: 1 }];
      ws.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: ws.columns.length },
      };

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, fileName);
    } catch (e) {
      console.error("Export failed:", e);
    } finally {
      setExportLoading(false);
    }
  };

  // Open filter panel with current committed values loaded into temp
  const openFilters = () => {
    setTmpPractices(selectedPractices);
    setTmpStatuses(selectedStatuses);
    setTmpAging(selectedAging);
    setTmpStartDate(startDate);
    setTmpEndDate(endDate);
    setTmpDateField(dateField);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setSelectedPractices(tmpPractices);
    setSelectedStatuses(tmpStatuses);
    setSelectedAging(tmpAging);
    setStartDate(tmpStartDate);
    setEndDate(tmpEndDate);
    setDateField(tmpDateField);
    setShowFilters(false);

    // Write filters to URL — preserve existing params (e.g. section=) then set/clear filter keys
    const params = new URLSearchParams(location.search);
    params.delete('practices'); params.delete('statuses'); params.delete('aging'); params.delete('start'); params.delete('end'); params.delete('df');
    if (tmpPractices.length) params.set('practices', tmpPractices.join(','));
    if (tmpStatuses.length) params.set('statuses', tmpStatuses.join(','));
    if (tmpAging.length) params.set('aging', tmpAging.join(','));
    if (tmpStartDate) params.set('start', tmpStartDate);
    if (tmpEndDate) params.set('end', tmpEndDate);
    if (tmpDateField === 'created') params.set('df', tmpDateField);
    navigate({ search: params.toString() }, { replace: true });
  };

  const activeCount =
    (selectedPractices.length ? 1 : 0) +
    (selectedStatuses.length ? 1 : 0) +
    (selectedAging.length ? 1 : 0) +
    (startDate && endDate ? 1 : 0);

  return (
    <Container fluid className="pt-2 acc-list">
      {/* Single top bar: Filter + Remove + Pagination + Excel + Share/Info (right-aligned) */}
      {/* Top bar: Totals (left, 50%) + Filter/Group/Pagination/Export/Info (right, 50%, bottom-aligned), directly above the data table */}
      {/* DESKTOP TOP-BAR (>= 768px) — Phase 37.1 Plan 02 Fix 3 wrapper.
          The existing Row with aged-bucket Table + action cluster is preserved verbatim. */}
      <div className="d-none d-lg-block">
        <Row className="mb-2">
          <Col md={6}>
            <Form.Label className="fw-semibold text-primary">
              Totals (all filtered rows)
            </Form.Label>
            <Table bordered size="sm" className="mb-0 text-center align-middle">
              <thead>
                <tr>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    Pending
                  </th>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    30 days
                  </th>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    60 days
                  </th>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    90 days
                  </th>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    120 days+
                  </th>
                  <th
                    style={{ width: "16.66%" }}
                    className="fw-semibold text-primary"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="acc-list-num">{currency.format(totals.sumPending)}</td>
                  <td className="acc-list-num">{currency.format(totals.sum30)}</td>
                  <td className="acc-list-num">{currency.format(totals.sum60)}</td>
                  <td className="acc-list-num">{currency.format(totals.sum90)}</td>
                  <td className="acc-list-num">{currency.format(totals.sum120)}</td>
                  <td className="acc-list-num">
                    <strong>{currency.format(totals.sumAll)}</strong>
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col md={6} className="d-flex gap-2 flex-wrap justify-content-end align-self-end">
            <Button
              variant="primary"
              onClick={openFilters}
              style={{ width: "120px" }}
            >
              <i className="fa-solid fa-filter me-2" />
              Filter{" "}
              {activeCount > 0 && (
                <Badge className="ms-2" bg="light" text="dark">
                  {activeCount}
                </Badge>
              )}
            </Button>
           {/*} <Button
              variant="outline-danger"
              onClick={clearFilters}
              style={{ width: "120px" }}
              disabled={
                selectedPractices.length === 0 &&
                selectedStatuses.length === 0 &&
                selectedAging.length === 0
              }
            >
              <i className="fa-solid fa-trash me-2" />
              Filters
            </Button> */}

            {/* Grouping toggle */}
            <Button
              variant={groupedView ? "primary" : "outline-primary"}
              onClick={() => setGroupedView((v) => !v)}
              style={{ width: "120px" }}
              title={
                groupedView ? "Switch to flat list" : "Switch to grouped view"
              }
            >
              <i
                className={`fa-solid ${groupedView ? "fa-list" : "fa-layer-group"} me-2`}
              />
              {groupedView ? "Flatten" : "Group"}
            </Button>

            {/* Pagination */}
            <div className="d-flex align-items-center gap-2 ms-2">
              <Button
                variant="primary"
                disabled={groupedView || page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <i className="fa-solid fa-angle-left" />
              </Button>
              <div className="text-nowrap small">
                {groupedView ? "Page 1 of 1" : `Page ${page} of ${totalPages}`}
              </div>
              <Button
                variant="primary"
                disabled={groupedView || page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <i className="fa-solid fa-angle-right" />
              </Button>
            </div>

            {/* Share + Info */}
            <ButtonGroup className="ms-1 gap-1">
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Export to Excel</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowExportModal(true)}
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <i className="fa-solid fa-file-export" />
                  )}
                </Button>
              </OverlayTrigger>

              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip>Info</Tooltip>}
              >
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowInfo(true)}
                >
                  <i className="fa-solid fa-circle-info" />
                </Button>
              </OverlayTrigger>
            </ButtonGroup>
          </Col>
        </Row>
      </div>

      {/* MOBILE TOP-BAR (< 768px) — Phase 37.1 Plan 02 Fix 3 + Fix 4.
          Row 1: compact aged-bucket totals strip (Fix 4).
          Row 2: action cluster (filter / group / pagination / export+info). */}
      <div className="d-lg-none mb-2">
        {/* Mobile totals — 4-column grid (label/amount × 2 pairs per row).
            Labels stack vertically in their column, amounts stack vertically in theirs,
            so the eye can scan down each column without re-anchoring. */}
        <div className="acc-list-mobile-summary mb-2">
          <span className="acc-list-mobile-summary-label">Pending</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sumPending)}</span>
          <span className="acc-list-mobile-summary-label">30 days</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sum30)}</span>

          <span className="acc-list-mobile-summary-label">60 days</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sum60)}</span>
          <span className="acc-list-mobile-summary-label">90 days</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sum90)}</span>

          <span className="acc-list-mobile-summary-label">120 days+</span>
          <span className="acc-list-mobile-summary-amount">{currency.format(totals.sum120)}</span>
          <span className="acc-list-mobile-summary-label acc-list-mobile-summary-label-total">Total</span>
          <span className="acc-list-mobile-summary-amount acc-list-mobile-summary-amount-total">{currency.format(totals.sumAll)}</span>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <Button
            variant="primary"
            onClick={openFilters}
            className="flex-grow-1"
            style={{ minWidth: 110 }}
          >
            <i className="fa-solid fa-filter me-2" />
            Filter{" "}
            {activeCount > 0 && (
              <Badge className="ms-2" bg="light" text="dark">
                {activeCount}
              </Badge>
            )}
          </Button>
          {/* Group/Flatten toggle is desktop-only — mobile card branch is FLAT by design
              (see comment on the mobile invoices.map below), so the toggle would break
              pagination without changing what the user sees. */}
          <div className="d-flex align-items-center gap-2 ms-auto">
            <Button
              variant="primary"
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <i className="fa-solid fa-angle-left" />
            </Button>
            <span className="text-nowrap small">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="primary"
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <i className="fa-solid fa-angle-right" />
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading Invoices...</p>
        </div>
      ) : invoices.length === 0 ? (
        <p>No invoices found.</p>
      ) : (
        <>
          {groupedView ? (
            <div className="d-none d-lg-block">
            <Table bordered hover responsive className="fixed-table">
              <thead className="text-center align-middle">
                <tr>
                  <th style={{ width: "2%" }}></th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Acc #
                  </th>
                  <th
                    style={{ width: "23%" }}
                    className="text-primary fw-semibold"
                  >
                    Account Holder
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Pending
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    30 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    60 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    90 D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    120+ D
                  </th>
                  <th
                    style={{ width: "6%" }}
                    className="text-primary fw-semibold"
                  >
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {groupedInvoices.map((group) => {
                  const isExpanded = expandedGroups.has(group.accountName);
                  const groupTotal =
                    group.sum30 + group.sum60 + group.sum90 + group.sum120;
                  return (
                    <React.Fragment key={group.accountName}>
                      {/* Account holder header row */}
                      <tr
                        style={{
                          cursor: "pointer",
                          backgroundColor: "#e8f0fe",
                        }}
                        onClick={() => toggleGroup(group.accountName)}
                      >
                        <td className="text-center align-middle">
                          <i
                            className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`}
                          />
                        </td>
                        <td>
                          {group.accountNumber}
                        </td>
                        <td>
                          {group.accountName}
                        </td>
                        <td className="text-end acc-list-num">
                          {group.sumPending > 0
                            ? currency.format(group.sumPending)
                            : ""}
                        </td>
                        <td className="text-end acc-list-num">
                          {group.sum30 > 0 ? currency.format(group.sum30) : ""}
                        </td>
                        <td className="text-end acc-list-num">
                          {group.sum60 > 0 ? currency.format(group.sum60) : ""}
                        </td>
                        <td className="text-end acc-list-num">
                          {group.sum90 > 0 ? currency.format(group.sum90) : ""}
                        </td>
                        <td className="text-end acc-list-num">
                          {group.sum120 > 0
                            ? currency.format(group.sum120)
                            : ""}
                        </td>
                        <td className="text-end fw-bold acc-list-num">
                          {groupTotal > 0 ? currency.format(groupTotal) : ""}
                        </td>
                      </tr>
                      {/* Collapsible invoice rows */}
                      {isExpanded &&
                        group.invoices.map((inv) => {
                          const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                          const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                          const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                          const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                          const adjustedTotal = (inv.total || 0) - totalCN - totalWO;
                          return (
                          <React.Fragment key={inv._id}>
                            <tr
                              style={{
                                cursor: "pointer",
                                "--bs-table-bg": expandedRows.has(inv._id)
                          ? "#CFE3FF4F"
                          : "",
                        "--bs-table-color": expandedRows.has(inv._id)
                          ? "#0f3490"
                          : "",
                        ...(expandedRows.has(inv._id)
                          ? {
                              color: "#cfe2ff",
                              borderBottom: "1px solid #0f3490",
                              fontWeight: 500,
                            }
                                  : {}),
                              }}
                              onClick={() => toggleRow(inv._id)}
                            >
                              <td></td>
                              <td></td>
                              <td>
                                <i
                                  className={`fa-solid fa-chevron-${expandedRows.has(inv._id) ? "down" : "right"} text-primary me-2`}
                                />
                                <span
                                  style={{
                                    cursor: "pointer",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(
                                      inv.status === "Pending"
                                        ? `/invoices/${inv._id}/${inv.accountId}`
                                        : `/invoices/posted/${inv._id}`,
                                      inv.status !== "Pending"
                                        ? { state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] } }
                                        : undefined
                                    );
                                  }}
                                >
                                  Inv #: {inv.invoiceNumber}
                                </span>
                                <span className="ms-2">
                                  {inv.invoiceDate
                                    ? new Date(
                                        inv.invoiceDate,
                                      ).toLocaleDateString()
                                    : ""}
                                </span>
                                <span className="ms-2 text-muted small">
                                  {inv.createdAt
                                    ? `Created ${new Date(inv.createdAt).toLocaleDateString()}`
                                    : ""}
                                </span>
                                <span className="ms-2">
                                  {inv.status}
                                </span>
                              </td>
                              <td className="text-end acc-list-num">
                                {inv.pending > 0
                                  ? currency.format(inv.pending)
                                  : ""}
                              </td>
                              <td className="text-end acc-list-num">
                                {inv.days30 > 0
                                  ? currency.format(inv.days30)
                                  : ""}
                              </td>
                              <td className="text-end acc-list-num">
                                {inv.days60 > 0
                                  ? currency.format(inv.days60)
                                  : ""}
                              </td>
                              <td className="text-end acc-list-num">
                                {inv.days90 > 0
                                  ? currency.format(inv.days90)
                                  : ""}
                              </td>
                              <td className="text-end acc-list-num">
                                {inv.days120plus > 0
                                  ? currency.format(inv.days120plus)
                                  : ""}
                              </td>
                              <td className="text-end fw-bold acc-list-num">
                                {adjustedTotal > 0 ? currency.format(adjustedTotal) : ""}
                              </td>
                            </tr>
                            {expandedRows.has(inv._id) && (
                              <tr>
                                <td colSpan={2}></td>
                                <td
                                  colSpan={7}
                                  style={{ padding: "0.25rem 0.5rem" }}
                                >
                                  {!inv.lines || inv.lines.length === 0 ? (
                                    <span className="text-muted small">
                                      No line items
                                    </span>
                                  ) : (
                                    <table
                                      className="table table-sm table-borderless mb-0"
                                      style={{
                                        fontSize: "0.85rem",
                                        tableLayout: "fixed",
                                        width: "100%",
                                      }}
                                    >
                                      <colgroup>
                                        <col style={{ width: "11%" }} />
                                        <col style={{ width: "40%" }} />
                                        <col style={{ width: "19%" }} />
                                        <col style={{ width: "5%" }} />
                                        <col style={{ width: "10%" }} />
                                      </colgroup>
                                      <tbody>
                                        {inv.lines.map((line, li) => (
                                          <tr key={li}>
                                            <td className="text-primary">{line.tariffCode || "-"}</td>
                                            <td className="text-primary">{line.description || "-"}</td>
                                            <td className="text-primary">
                                              {line.serviceProvider
                                                ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                                : inv.practitioner?.firstName
                                                  ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                                  : "-"}
                                            </td>
                                            <td className="text-center text-primary">
                                              {line.quantity ?? 1}
                                            </td>
                                            <td className="text-end text-primary">
                                              {currency.format(line.rate || 0)}
                                            </td>
                                          </tr>
                                        ))}
                                        {creditNotes.map((r, ci) => (
                                          <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                            <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                              Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                            </td>
                                            <td colSpan={1}></td>
                                            <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                          </tr>
                                        ))}
                                        {writeOffs.map((r, wi) => (
                                          <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                            <td colSpan={3} style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                              Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                            </td>
                                            <td colSpan={1}></td>
                                            <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                          );
                        })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
            </div>
          ) : (
            <div className="d-none d-lg-block">
            <Table bordered hover responsive className="fixed-table">
              <thead className="text-center align-middle ms-1 me-1">
                <tr>
                  <th style={{ width: "3%" }}></th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("invoiceDate")}
                    className="text-primary fw-semibold"
                  >
                    Date {renderSortIcon("invoiceDate")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("createdAt")}
                    className="text-primary fw-semibold"
                  >
                    Created {renderSortIcon("createdAt")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "9%" }}
                    onClick={() => handleSort("invoiceNumber")}
                    className="text-primary fw-semibold"
                  >
                    Inv Number {renderSortIcon("invoiceNumber")}
                  </th>
                  <th
                    style={{ width: "14%" }}
                    className="text-primary fw-semibold"
                  >
                    Practice Name
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "14%" }}
                    onClick={() => handleSort("accountName")}
                    className="text-primary fw-semibold"
                  >
                    Account Name {renderSortIcon("accountName")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "10%" }}
                    onClick={() => handleSort("status")}
                    className="text-primary fw-semibold"
                  >
                    Status {renderSortIcon("status")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("pending")}
                    className="text-primary fw-semibold"
                  >
                    Pend {renderSortIcon("pending")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days30")}
                    className="text-primary fw-semibold"
                  >
                    30 D {renderSortIcon("days30")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days60")}
                    className="text-primary fw-semibold"
                  >
                    60 D {renderSortIcon("days60")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days90")}
                    className="text-primary fw-semibold"
                  >
                    90 D {renderSortIcon("days90")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("days120plus")}
                    className="text-primary fw-semibold"
                  >
                    120+ D {renderSortIcon("days120plus")}
                  </th>
                  <th
                    style={{ cursor: "pointer", width: "8%" }}
                    onClick={() => handleSort("total")}
                    className="text-primary fw-semibold"
                  >
                    Total {renderSortIcon("total")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
                  const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
                  const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
                  const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
                  const adjustedTotal = (inv.total || 0) - totalCN - totalWO;
                  return (
                  <React.Fragment key={inv._id}>
                    <tr
                      style={{
                        cursor: "pointer",
                        "--bs-table-bg": expandedRows.has(inv._id)
                          ? "#CFE3FF4F"
                          : "",
                        "--bs-table-color": expandedRows.has(inv._id)
                          ? "#0f3490"
                          : "",
                        ...(expandedRows.has(inv._id)
                          ? {
                              color: "#cfe2ff",
                              borderTop: "1px solid #0f3490",
                              fontWeight: 500,
                            }
                          : {}),
                      }}
                      onClick={() => toggleRow(inv._id)}
                    >
                      <td className="text-center align-middle">
                        <i
                          className={`fa-solid fa-chevron-${expandedRows.has(inv._id) ? "down" : "right"} text-primary`}
                        />
                      </td>
                      <td>
                        {inv.invoiceDate
                          ? new Date(inv.invoiceDate).toLocaleDateString()
                          : ""}
                      </td>
                      <td>
                        {inv.createdAt
                          ? new Date(inv.createdAt).toLocaleDateString()
                          : ""}
                      </td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            inv.status === "Pending"
                              ? `/invoices/${inv._id}/${inv.accountId}`
                              : `/invoices/posted/${inv._id}`,
                            inv.status !== "Pending"
                              ? { state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] } }
                              : undefined
                          );
                        }}
                      >
                        {inv.invoiceNumber}
                      </td>
                      <td>{inv.practice?.practiceName}</td>
                      <td
                        style={{ cursor: "pointer" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/accounts/${inv.accountId}`, {
                            state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] },
                          });
                        }}
                      >
                        {inv.accountName}
                      </td>
                      <td>{inv.status}</td>
                      <td className="text-end acc-list-num">
                        {inv.pending > 0 ? currency.format(inv.pending) : ""}
                      </td>
                      <td className="text-end acc-list-num">
                        {inv.days30 > 0 ? currency.format(inv.days30) : ""}
                      </td>
                      <td className="text-end acc-list-num">
                        {inv.days60 > 0 ? currency.format(inv.days60) : ""}
                      </td>
                      <td className="text-end acc-list-num">
                        {inv.days90 > 0 ? currency.format(inv.days90) : ""}
                      </td>
                      <td className="text-end acc-list-num">
                        {inv.days120plus > 0
                          ? currency.format(inv.days120plus)
                          : ""}
                      </td>
                      <td className="text-end fw-bold acc-list-num">
                        {adjustedTotal > 0 ? currency.format(adjustedTotal) : ""}
                      </td>
                    </tr>
                    {expandedRows.has(inv._id) && (
                      <tr key={`${inv._id}-lines`}>
                        <td colSpan={3}></td>
                        <td colSpan={10} style={{ padding: "0.25rem 0.5rem" }}>
                          {!inv.lines || inv.lines.length === 0 ? (
                            <span className="text-muted small">
                              No line items
                            </span>
                          ) : (
                            <table
                              className="table table-sm table-borderless mb-0"
                              style={{
                                fontSize: "0.85rem",
                                tableLayout: "fixed",
                                width: "100%",
                              }}
                            >
                              <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "45%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "5%" }} />
                                <col style={{ width: "10%" }} />
                              </colgroup>
                              <tbody>
                                {inv.lines.map((line, li) => (
                                  <tr key={li}>
                                    <td className="text-primary">{line.tariffCode || line.nappiCode || "-"}</td>
                                    <td className="text-primary">{line.description || "-"}</td>
                                    <td className="text-primary">
                                      {line.serviceProvider
                                        ? `${line.serviceProvider.firstName} ${line.serviceProvider.lastName}`.trim()
                                        : inv.practitioner?.firstName
                                          ? `${inv.practitioner.firstName} ${inv.practitioner.lastName || ""}`.trim()
                                          : "-"}
                                    </td>
                                    <td className="text-center text-primary">
                                      {line.quantity ?? 1}
                                    </td>
                                    <td className="text-end text-primary">
                                      {currency.format(line.rate || 0)}
                                    </td>
                                  </tr>
                                ))}
                                {creditNotes.map((r, ci) => (
                                  <tr key={`cn-${ci}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Credit Note {r.creditNoteNumber}{r.creditNoteDate ? ` — ${new Date(r.creditNoteDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.creditNoteAmount || 0)})</td>
                                  </tr>
                                ))}
                                {writeOffs.map((r, wi) => (
                                  <tr key={`wo-${wi}`} style={{ color: "#6c757d" }}>
                                    <td colSpan={3} className="text-danger" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                                      Write Off {r.writeOffNumber}{r.writeOffDate ? ` — ${new Date(r.writeOffDate).toLocaleDateString()}` : ""}
                                    </td>
                                    <td colSpan={1}></td>
                                    <td className="text-end text-danger">({currency.format(r.writeOffAmount || 0)})</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
            </div>
          )}
          {/* MOBILE CARD BRANCH (Phase 37) — FLAT cards only, ignores groupedView per 37-RESEARCH.md Pitfall 3 */}
          <div className="d-lg-none">
            {invoices.map((inv) => {
              const creditNotes = (inv.receipts || []).filter((r) => r.creditNoteNumber);
              const writeOffs = (inv.receipts || []).filter((r) => r.writeOffNumber);
              const totalCN = creditNotes.reduce((s, r) => s + (r.creditNoteAmount || 0), 0);
              const totalWO = writeOffs.reduce((s, r) => s + (r.writeOffAmount || 0), 0);
              const adjustedTotal = (inv.total || 0) - totalCN - totalWO;
              const isExpanded = expandedRows.has(inv._id);

              return (
                <div className="acc-card" key={inv._id}>
                  <div className="acc-card-top">
                    <span className="acc-card-id">{inv.invoiceNumber}</span>
                    <span className="acc-card-amount">
                      {adjustedTotal > 0 ? currency.format(adjustedTotal) : ""}
                    </span>
                    <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
                      <Dropdown.Toggle
                        variant="link"
                        className="acc-card-kebab text-primary text-decoration-none p-0 border-0"
                        aria-label="Row actions"
                      >
                        <i className="fa-solid fa-ellipsis-vertical" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        align="end"
                        className="shadow-sm"
                      >
                        <Dropdown.Item
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(
                              inv.status === "Pending"
                                ? `/invoices/${inv._id}/${inv.accountId}`
                                : `/invoices/posted/${inv._id}`,
                              inv.status !== "Pending"
                                ? { state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] } }
                                : undefined
                            );
                          }}
                        >
                          <i className="fa-solid fa-file-invoice me-2" /> Open Invoice
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/accounts/${inv.accountId}`, {
                              state: { breadcrumbTrail: [{ label: 'Invoice List', path: `${location.pathname}${location.search}` }] },
                            });
                          }}
                        >
                          <i className="fa-solid fa-user me-2" /> Open Account
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>

                  {/* Aged-bucket summary — per OQ-2 RESOLVED. Each non-zero bucket renders as
                      a label-left/amount-right row mirroring .acc-card-field for visual consistency. */}
                  {(inv.pending > 0 || inv.days30 > 0 || inv.days60 > 0 || inv.days90 > 0 || inv.days120plus > 0) && (
                    <div className="acc-card-buckets">
                      {inv.pending > 0 && (
                        <div className="acc-card-field">
                          <span className="acc-card-label">Pending</span>
                          <span className="acc-card-value acc-card-bucket-amount">{currency.format(inv.pending)}</span>
                        </div>
                      )}
                      {inv.days30 > 0 && (
                        <div className="acc-card-field">
                          <span className="acc-card-label">30d</span>
                          <span className="acc-card-value acc-card-bucket-amount">{currency.format(inv.days30)}</span>
                        </div>
                      )}
                      {inv.days60 > 0 && (
                        <div className="acc-card-field">
                          <span className="acc-card-label">60d</span>
                          <span className="acc-card-value acc-card-bucket-amount">{currency.format(inv.days60)}</span>
                        </div>
                      )}
                      {inv.days90 > 0 && (
                        <div className="acc-card-field">
                          <span className="acc-card-label">90d</span>
                          <span className="acc-card-value acc-card-bucket-amount">{currency.format(inv.days90)}</span>
                        </div>
                      )}
                      {inv.days120plus > 0 && (
                        <div className="acc-card-field">
                          <span className="acc-card-label">120d+</span>
                          <span className="acc-card-value acc-card-bucket-amount">{currency.format(inv.days120plus)}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div
                    className="acc-card-body"
                    onClick={() => toggleRow(inv._id)}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="acc-card-field">
                      <span className="acc-card-label">Date</span>
                      <span className="acc-card-value">
                        {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Created</span>
                      <span className="acc-card-value">
                        {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : ""}
                      </span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Practice</span>
                      <span className="acc-card-value">{inv.practice?.practiceName}</span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Account</span>
                      <span className="acc-card-value">{inv.accountName}</span>
                    </div>
                    <div className="acc-card-field">
                      <span className="acc-card-label">Status</span>
                      <span className="acc-card-value">
                        <Badge bg="secondary">{inv.status}</Badge>
                      </span>
                    </div>

                    <div className="acc-card-expand-hint">
                      <i className={`fa-solid fa-chevron-${isExpanded ? "down" : "right"} text-primary`} />
                      <span className="ms-2 small text-muted">
                        {isExpanded ? "Hide line items" : "Show line items"}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="acc-card-lines">
                      {(inv.lines || []).map((line, li) => (
                        <div className="acc-card-line" key={`line-${li}`}>
                          <span className="acc-card-line-code">{line.tariffCode || "-"}</span>
                          <span className="acc-card-line-rate">{currency.format(line.rate || 0)}</span>
                          <span className="acc-card-line-desc">{line.description || "-"}</span>
                        </div>
                      ))}
                      {creditNotes.map((r, ci) => (
                        <div className="acc-card-line" key={`cn-${ci}`}>
                          <span className="acc-card-line-code">CN {r.creditNoteNumber}</span>
                          <span className="acc-card-line-rate">{currency.format(-(r.creditNoteAmount || 0))}</span>
                        </div>
                      ))}
                      {writeOffs.map((r, wi) => (
                        <div className="acc-card-line" key={`wo-${wi}`}>
                          <span className="acc-card-line-code">WO {r.writeOffNumber}</span>
                          <span className="acc-card-line-rate">{currency.format(-(r.writeOffAmount || 0))}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Export type modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)} centered>
        <Modal.Header
          closeButton
          style={{ background: "#CFE2FF" }}
          className="rounded-0 text-primary d-flex align-items-center"
        >
          <Modal.Title as="h6" className="m-0">
            Export to Excel
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">Choose what to include in the spreadsheet:</p>
          <div className="d-grid gap-2">
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("summary")}
            >
              <div className="fw-semibold">Summary</div>
              <small>One row per invoice — totals and aged buckets only.</small>
            </Button>
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("lines")}
            >
              <div className="fw-semibold">Invoice Lines</div>
              <small>Just the billed line items across all invoices.</small>
            </Button>
            <Button
              variant="outline-primary"
              className="text-start"
              onClick={() => handleExportExcel("full")}
            >
              <div className="fw-semibold">Invoices with Lines</div>
              <small>Full report — invoices, lines, receipts, credit notes and write-offs.</small>
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Info modal */}
      <Modal show={showInfo} onHide={() => setShowInfo(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Aged Invoices</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-muted">
          <p>
            Click on the <strong>Invoice Number</strong> to navigate to the
            Invoice and the <strong>Account Name</strong> to navigate to the
            Account.
          </p>
          <p className="mb-0">
            Use the filter panel to refine the dataset. Totals reflect all
            filtered rows (not just this page). Excel export downloads the
            entire filtered dataset.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowInfo(false)}>
            Got it
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Filter Offcanvas (unchanged) */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title className="test-primary d-flex align-items-center">
            <i className="fa-solid fa-filter me-2 text-primary" />
            Filters
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mb-3">
            <h6 className="text-primary fw-normal ms-1 mb-2">Date range</h6>
            <div className="d-flex gap-2 mb-2 ms-1">
              <Button
                size="sm"
                variant={tmpDateField === "invoice" ? "primary" : "outline-primary"}
                onClick={() => setTmpDateField("invoice")}
                style={{ minWidth: 120 }}
              >
                Invoice date
              </Button>
              <Button
                size="sm"
                variant={tmpDateField === "created" ? "primary" : "outline-primary"}
                onClick={() => setTmpDateField("created")}
                style={{ minWidth: 120 }}
              >
                Created date
              </Button>
            </div>
            <div className="d-flex gap-2 ms-1">
              <Form.Control
                type="date"
                value={tmpStartDate}
                onChange={(e) => setTmpStartDate(e.target.value)}
              />
              <Form.Control
                type="date"
                value={tmpEndDate}
                onChange={(e) => setTmpEndDate(e.target.value)}
              />
            </div>
          </div>
          <Accordion alwaysOpen>
            {/* Practices */}
            <Accordion.Item eventKey="0">
              <Accordion.Header>Practices</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices(allPractices)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpPractices([])}
                  >
                    Clear
                  </Button>
                </div>
                <div
                  className="d-flex flex-column"
                  style={{ maxHeight: 240, overflowY: "auto" }}
                >
                  {allPractices.map((name) => (
                    <Form.Check
                      key={name}
                      type="checkbox"
                      id={`practice-${name}`}
                      label={name}
                      checked={tmpPractices.includes(name)}
                      onChange={() =>
                        setTmpPractices((arr) => toggleIn(arr, name))
                      }
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Statuses */}
            <Accordion.Item eventKey="1">
              <Accordion.Header>Status</Accordion.Header>
              <Accordion.Body>
                <div className="mb-2 d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses(statusOptions)}
                  >
                    Select all
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setTmpStatuses([])}
                  >
                    Clear
                  </Button>
                </div>
                <div className="d-flex flex-column">
                  {statusOptions.map((s) => (
                    <Form.Check
                      key={s}
                      type="checkbox"
                      id={`status-${s}`}
                      label={s}
                      checked={tmpStatuses.includes(s)}
                      onChange={() => setTmpStatuses((arr) => toggleIn(arr, s))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>

            {/* Aging */}
            <Accordion.Item eventKey="2">
              <Accordion.Header>Aging</Accordion.Header>
              <Accordion.Body>
                <div className="d-flex flex-column">
                  {["30", "60", "90", "120"].map((a) => (
                    <Form.Check
                      key={a}
                      type="checkbox"
                      id={`aging-${a}`}
                      label={`${a} Days${a === "120" ? "+" : ""}`}
                      checked={tmpAging.includes(a)}
                      onChange={() => setTmpAging((arr) => toggleIn(arr, a))}
                    />
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>

          {/* Actions */}
          <div className="mt-4 d-flex gap-2">
            <Button onClick={applyFilters} style={{ width: "120px" }}>
              <i className="fa-solid fa-filter me-2 text-white" />
              Apply
            </Button>
            <Button
              style={{ width: "120px" }}
              variant="outline-danger"
              onClick={() => {
                setTmpPractices([]);
                setTmpStatuses([]);
                setTmpAging([]);
                setTmpStartDate("");
                setTmpEndDate("");
                setTmpDateField("invoice");
              }}
            >
              <i className="fa-solid fa-trash me-2" />
              Remove
            </Button>
          </div>
          <div className="mt-2 text-muted small">
            Tip: leaving a section empty means "All".
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
